import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Avatar from '@material-ui/core/Avatar';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Tooltip, Chip } from '@mui/material';
import {
  CircularProgress,
  TableRow,
  TableCell,
  TableBody,
  Table,
  TableHead,
  TableFooter,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'immutable';
import isAfter from 'date-fns/isAfter';

import { convertDuration } from 'utils/convertDuration';
import Deed from 'entities/deed/model';
import Organization from 'entities/organization/model';
import VolunteerTime from 'entities/volunteerTime/model';
import User from 'entities/user/model';
import { TableBox } from 'retired/Box';
import { truthy } from 'utils/truthy';
import DeedRole from 'entities/deed/role';
import { purpleLighten20, white } from 'utils/color-styles';

import { RemoveUserButton } from './RemoveUserButton';
import {
  approveVolunteerTime,
  checkInAttendeeAction,
  denyVolunteerTime,
  setUserStatusAction,
  toggleFormResponsesOverlayAction,
} from './actions';
import { selectVolunteerTimeResponding } from './selectors';

interface Props {
  attendees: User[];
  deed: Deed;
  organization: Organization;
  volunteerTimes: List<VolunteerTime>;
  role?: DeedRole | undefined;
  title: string;
}

export const AttendeesTable = ({
  attendees,
  deed,
  organization,
  volunteerTimes,
  role,
  title,
}: Props) => {
  const dispatch = useDispatch();
  const volunteerTimeResponding = useSelector(selectVolunteerTimeResponding);

  if (!attendees.length) {
    return null;
  }
  const hasNonGlobalFormsWithResponses = deed?.formResponses?.size > 0;

  const hasVolunteerTimes = volunteerTimes?.size > 0;

  const isCheckInAllowed =
    deed.type === 'Event' && deed.startingAt
      ? isAfter(new Date(), new Date(deed.startingAt.getTime() - 30 * 60 * 1000))
      : true;

  const columns = [
    'Name',
    'Email',
    hasNonGlobalFormsWithResponses && 'Responses',
    deed.type === 'Event' && 'Status',
    hasVolunteerTimes && 'Hours',
    ' ',
  ].filter(truthy);

  return (
    <TableBox style={{ overflowX: 'auto' }}>
      <Table size="small" className="colored purpleLighten20">
        <TableHead>
          <TableRow>
            <TableCell colSpan={columns.length} style={{ padding: 0, border: 0 }}>
              <div
                style={{
                  background: purpleLighten20,
                  width: '100%',
                  padding: '16px 16px 6px 16px',
                  color: white,
                  fontSize: '17px',
                }}
              >
                {title}
              </div>
            </TableCell>
          </TableRow>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {attendees.map((attendeeMaybeImmutable) => {
            const attendee = attendeeMaybeImmutable?.toJS?.() ?? attendeeMaybeImmutable;

            const isPending = role
              ?.get('applicants')
              .map((u) => u.get('id'))
              .includes(attendee.id);
            const isApproved = role
              ?.get('approved')
              .map((u) => u.get('id'))
              .includes(attendee.id);
            const isRejected = role
              ?.get('rejected')
              .map((u) => u.get('id'))
              .includes(attendee.id);
            return (
              <Fragment key={attendee.id}>
                <TableRow>
                  <TableCell
                    style={{
                      width: '40%',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      lineHeight: '32px',
                    }}
                  >
                    <Avatar
                      alt={attendee.name}
                      src={attendee.mainPicture?.replace('http://', 'https://')}
                      style={{
                        width: 32,
                        height: 32,
                        float: 'left',
                        marginRight: 10,
                      }}
                    />
                    <Link to={`/volunteers/${attendee.id}`}>{attendee.name}</Link>
                    {deed.partner && deed.public && attendee?.organization !== organization.id
                      ? ' (normal user)'
                      : ''}
                  </TableCell>
                  <TableCell
                    style={{
                      width: '40%',
                      maxWidth: 100,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {attendee.email || attendee.facebook?.email}
                  </TableCell>
                  {hasNonGlobalFormsWithResponses && (
                    <TableCell style={{ textAlign: 'center' }}>
                      <IconButton
                        onClick={() => dispatch(toggleFormResponsesOverlayAction(attendee.id))}
                        size="small"
                        title="Attendee's form response"
                        color="primary"
                      >
                        <AssignmentIcon />
                      </IconButton>
                    </TableCell>
                  )}
                  {deed.type === 'Event' && (
                    <TableCell style={{ width: '20%', minWidth: 130 }}>
                      {deed.isExternalDeed ? (
                        deed.isUserAttending(attendee) ? (
                          'Volunteer'
                        ) : (
                          'Showed interest'
                        )
                      ) : deed.checkIns.includes(attendee.id) ? (
                        'Checked in'
                      ) : (
                        <Tooltip
                          title="The check-in option will become available 30 minutes before the event starts."
                          arrow
                          disableHoverListener={isCheckInAllowed}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                              variant="outlined"
                              label="Check in"
                              disabled={!isCheckInAllowed}
                              onClick={() =>
                                dispatch(checkInAttendeeAction(deed.id, attendee.id, deed.checkIns))
                              }
                            />
                          </div>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                  {hasVolunteerTimes && <TableCell />}
                  <TableCell style={{ padding: 0, textAlign: 'right' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        marginRight: 5,
                      }}
                    >
                      {deed.type === 'Project' && role && (
                        <>
                          <Button
                            disabled={isApproved}
                            color={isPending ? 'primary' : 'default'}
                            variant={isApproved ? 'contained' : undefined}
                            onClick={() =>
                              dispatch(
                                setUserStatusAction(
                                  {
                                    status: 'approved',
                                    roleId: role.id,
                                  },
                                  deed.get('id'),
                                  attendee.id
                                )
                              )
                            }
                            style={{ marginRight: 5 }}
                            size="small"
                          >
                            {isApproved ? 'Approved' : 'Approve'}
                          </Button>
                          {!role?.get('autoApproval') && (
                            <Button
                              disabled={isRejected}
                              color={isPending ? 'primary' : 'default'}
                              variant={isRejected ? 'contained' : undefined}
                              onClick={() =>
                                dispatch(
                                  setUserStatusAction(
                                    {
                                      status: 'rejected',
                                      roleId: role.id,
                                    },
                                    deed.get('id'),
                                    attendee.id
                                  )
                                )
                              }
                              size="small"
                            >
                              {isRejected ? 'Rejected' : 'Reject'}
                            </Button>
                          )}
                        </>
                      )}

                      {!deed.isExternalDeed && deed.isUserAttending(attendee) && (
                        <RemoveUserButton
                          deed={deed}
                          type="attendee"
                          userId={attendee.id}
                          roleId={role?.id}
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                {volunteerTimes
                  .filter((volunteerTime) => volunteerTime?.user?.id === attendee.id)
                  .toArray()
                  .map((volunteerTime) => (
                    <TableRow key={volunteerTime.id}>
                      <TableCell colSpan={columns.length - 2} />
                      <TableCell>{convertDuration(volunteerTime.duration)}</TableCell>
                      <TableCell>
                        {volunteerTimeResponding.get(volunteerTime.id) ? (
                          <CircularProgress size={24} />
                        ) : (
                          <div style={{ display: 'flex' }}>
                            <Button
                              variant={
                                volunteerTime.approval === 'Approved' ? 'contained' : undefined
                              }
                              onClick={() => dispatch(approveVolunteerTime(volunteerTime.id))}
                              disabled={volunteerTime.approval === 'Approved'}
                              style={{ marginRight: 5 }}
                              size="small"
                            >
                              {volunteerTime.approval === 'Approved' ? 'Approved' : 'Approve'}
                            </Button>
                            <Button
                              variant={
                                volunteerTime.approval === 'Rejected' ? 'contained' : undefined
                              }
                              onClick={() => dispatch(denyVolunteerTime(volunteerTime.id))}
                              disabled={
                                volunteerTime.approval === 'Approved' ||
                                volunteerTime.approval === 'Rejected'
                              }
                              style={{ marginRight: 5 }}
                              size="small"
                            >
                              {volunteerTime.approval === 'Rejected' ? 'Rejected' : 'Reject'}
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </Fragment>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell
              size="medium"
              style={{ color: '#0000008a', fontSize: '0.875rem' }}
              colSpan={columns.length}
            >
              Total: {attendees.length}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableBox>
  );
};
